// Helpers WebAuthn (biométrie navigateur : empreinte / Face ID / Windows Hello)

const b64uToBuf = (b64u) => {
  const b64 = b64u.replace(/-/g, "+").replace(/_/g, "/");
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const bin = window.atob(b64 + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const bufToB64u = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const credToJSON = (cred) => ({
  id: cred.id,
  rawId: bufToB64u(cred.rawId),
  type: cred.type,
  clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  response: {
    clientDataJSON: bufToB64u(cred.response.clientDataJSON),
    ...(cred.response.attestationObject ? { attestationObject: bufToB64u(cred.response.attestationObject) } : {}),
    ...(cred.response.authenticatorData ? { authenticatorData: bufToB64u(cred.response.authenticatorData) } : {}),
    ...(cred.response.signature ? { signature: bufToB64u(cred.response.signature) } : {}),
    ...(cred.response.userHandle ? { userHandle: bufToB64u(cred.response.userHandle) } : {}),
  },
});

export const webauthnSupported = () =>
  typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials;

// L'ingress réécrit le header Origin : on transmet l'origine réelle du navigateur
export const bioHeaders = () => ({ headers: { "X-Client-Origin": window.location.origin } });

// Enrôlement de la biométrie de l'appareil
export async function enrollBiometric(api) {
  if (!webauthnSupported()) throw new Error("Ce navigateur ne prend pas en charge la biométrie (WebAuthn).");
  const { data: options } = await api.post("/biometric/register/options", {}, bioHeaders());
  options.challenge = b64uToBuf(options.challenge);
  options.user.id = b64uToBuf(options.user.id);
  if (options.excludeCredentials) options.excludeCredentials = options.excludeCredentials.map(c => ({ ...c, id: b64uToBuf(c.id) }));
  const cred = await navigator.credentials.create({ publicKey: options });
  if (!cred) throw new Error("Enrôlement annulé");
  await api.post("/biometric/register/verify", credToJSON(cred), bioHeaders());
}

// Vérification biométrique (step-up avant pointage)
export async function verifyBiometric(api) {
  if (!webauthnSupported()) throw new Error("Ce navigateur ne prend pas en charge la biométrie (WebAuthn).");
  const { data: options } = await api.post("/biometric/auth/options", {}, bioHeaders());
  options.challenge = b64uToBuf(options.challenge);
  if (options.allowCredentials) options.allowCredentials = options.allowCredentials.map(c => ({ ...c, id: b64uToBuf(c.id) }));
  const cred = await navigator.credentials.get({ publicKey: options });
  if (!cred) throw new Error("Vérification annulée");
  await api.post("/biometric/auth/verify", credToJSON(cred), bioHeaders());
}

// Exécute une action de pointage ; si le backend exige la biométrie (HTTP 428),
// déclenche la vérification puis réessaie une fois.
export async function withBiometric(api, fn) {
  try {
    return await fn();
  } catch (e) {
    if (e.response?.status === 428) {
      await verifyBiometric(api);
      return await fn();
    }
    throw e;
  }
}
